import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderAPI, customerAPI } from '../services/api';
import { getCurrentLocation } from '../utils/location';
import toast from 'react-hot-toast';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface CustomerInfo {
  name: string;
  phone: string;
  address: string;
  landmark?: string;
  gpsCoordinates?: string;
}

interface CheckoutPageProps {
  currentUser?: any;
  onAuthRequired?: () => boolean;
  deliveryInfo?: any;
}

const CheckoutPage = ({ currentUser, onAuthRequired, deliveryInfo }: CheckoutPageProps) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    phone: '',
    address: '',
    landmark: '',
    gpsCoordinates: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('cash_on_delivery');
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('deliveryCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    } else {
      navigate('/menu');
    }
  }, [navigate]);

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const deliveryFee = deliveryInfo?.deliveryFee || 30;
  const totalAmount = getCartTotal() + deliveryFee;

  const handleInputChange = (field: keyof CustomerInfo, value: string) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getCurrentLocationAddress = async () => {
    try {
      setLocationLoading(true);
      const location = await getCurrentLocation();
      
      const coordinates = `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`;
      
      setCustomerInfo(prev => ({
        ...prev,
        gpsCoordinates: coordinates
      }));
      
      toast.success('GPS location detected');
    } catch (error) {
      toast.error('Failed to get location');
    } finally {
      setLocationLoading(false);
    }
  };

  const validateForm = (): boolean => {
    if (!customerInfo.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    
    if (!customerInfo.phone.trim() || customerInfo.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    
    if (!customerInfo.address.trim() && !customerInfo.gpsCoordinates?.trim()) {
      toast.error('Please enter your delivery address or use GPS location');
      return false;
    }
    
    return true;
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Create/get customer
      let customer;
      try {
        customer = await customerAPI.getByPhone(customerInfo.phone);
      } catch (error) {
        // Customer doesn't exist, create new one
        customer = await customerAPI.create({
          name: customerInfo.name,
          phone: customerInfo.phone,
          address: customerInfo.address,
          email: `${customerInfo.phone}@delivery.local` // Placeholder email
        });
      }

      // Create order
      const orderData = {
        customer_id: customer.data?.id,
        customerName: customerInfo.name,
        customerPhone: customerInfo.phone,
        customerEmail: `${customerInfo.phone}@delivery.local`,
        deliveryAddress: customerInfo.address || 'GPS Location',
        gpsCoordinates: customerInfo.gpsCoordinates || '',
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        subtotal: getCartTotal(),
        taxAmount: 0,
        deliveryFee: deliveryFee,
        totalAmount: totalAmount,
        paymentMethod: paymentMethod,
        notes: customerInfo.landmark || ''
      };

      const orderResponse = await orderAPI.create(orderData);
      
      console.log('Order response in CheckoutPage:', orderResponse);
      console.log('Navigating to order ID:', orderResponse.data?.id);
      
      // Clear cart
      localStorage.removeItem('deliveryCart');
      
      toast.success('Order placed successfully!');
      navigate(`/order/${orderResponse.data.id}`);
      
    } catch (error) {
      console.error('Failed to place order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', paddingBottom: '120px' }}>
      {/* Header */}
      <header style={{ 
        background: 'white', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
        position: 'sticky', 
        top: 0, 
        zIndex: 10 
      }}>
        <div style={{ 
          maxWidth: '448px',
          margin: '0 auto',
          padding: '16px', 
          display: 'flex', 
          alignItems: 'center' 
        }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{ 
              padding: '8px', 
              marginLeft: '-8px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ←
          </button>
          <h1 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#111827', 
            marginLeft: '16px',
            margin: 0
          }}>
            Checkout
          </h1>
        </div>
      </header>

      <div style={{ 
        maxWidth: '448px',
        margin: '0 auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>
        {/* Customer Information */}
        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '16px',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            👤 Customer Information
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '4px' 
              }}>
                Full Name *
              </label>
              <input
                type="text"
                value={customerInfo.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter your name"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f97316'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '4px' 
              }}>
                Phone Number *
              </label>
              <input
                type="tel"
                value={customerInfo.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Enter your phone number"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f97316'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
          </div>
        </div>

        {/* Delivery Address */}
        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '16px',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📍 Delivery Address
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '4px' 
              }}>
                Full Address *
              </label>
              <textarea
                value={customerInfo.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Enter your complete address"
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f97316'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '4px' 
              }}>
                Landmark (Optional)
              </label>
              <input
                type="text"
                value={customerInfo.landmark}
                onChange={(e) => handleInputChange('landmark', e.target.value)}
                placeholder="Nearby landmark for easy delivery"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f97316'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
            
            <button
              onClick={getCurrentLocationAddress}
              disabled={locationLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#f97316',
                fontSize: '14px',
                fontWeight: '500',
                background: 'none',
                border: 'none',
                cursor: locationLoading ? 'not-allowed' : 'pointer',
                opacity: locationLoading ? 0.6 : 1,
                padding: '4px 0'
              }}
            >
              📍 <span>{locationLoading ? 'Getting location...' : 'Use current location'}</span>
            </button>
            
            {customerInfo.gpsCoordinates && (
              <div style={{
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '8px'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#0369a1',
                  marginBottom: '4px'
                }}>
                  📍 GPS Location Captured
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: '#075985', 
                  fontFamily: 'monospace'
                }}>
                  {customerInfo.gpsCoordinates}
                </div>
                <button
                  onClick={() => setCustomerInfo(prev => ({ ...prev, gpsCoordinates: '' }))}
                  style={{
                    fontSize: '12px',
                    color: '#dc2626',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    marginTop: '4px',
                    padding: 0
                  }}
                >
                  Clear GPS location
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Payment Method */}
        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>
            Payment Method
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="payment"
                value="cash_on_delivery"
                checked={paymentMethod === 'cash_on_delivery'}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{
                  marginRight: '12px',
                  accentColor: '#f97316'
                }}
              />
              <span style={{ color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                💵 Cash on Delivery
              </span>
            </label>
            
            <label style={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="payment"
                value="upi"
                checked={paymentMethod === 'upi'}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{
                  marginRight: '12px',
                  accentColor: '#f97316'
                }}
              />
              <span style={{ color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📱 UPI Payment
              </span>
            </label>
          </div>
        </div>

        {/* Order Summary */}
        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>
            Order Summary
          </h2>
          
          <div style={{ marginBottom: '16px' }}>
            {cart.map(item => (
              <div key={item.id} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                fontSize: '14px',
                marginBottom: '8px',
                color: '#374151'
              }}>
                <span>{item.name} x {item.quantity}</span>
                <span>₹{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          
          <div style={{ 
            borderTop: '1px solid #e5e7eb', 
            paddingTop: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }}>
              <span>Subtotal</span>
              <span>₹{getCartTotal().toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }}>
              <span>
                Delivery Fee 
                {deliveryInfo?.zone && (
                  <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '4px' }}>
                    ({deliveryInfo.distance} km - {deliveryInfo.zone.name})
                  </span>
                )}
              </span>
              <span>₹{deliveryFee.toFixed(2)}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#111827',
              borderTop: '1px solid #e5e7eb',
              paddingTop: '8px',
              marginTop: '8px'
            }}>
              <span>Total</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Place Order Button */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '448px',
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        padding: '16px'
      }}>
        <button
          onClick={handlePlaceOrder}
          disabled={loading}
          style={{
            width: '100%',
            background: loading ? '#9ca3af' : '#f97316',
            color: 'white',
            padding: '16px',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '18px',
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {loading ? 'Placing Order...' : `Place Order - ₹${totalAmount.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
};

export default CheckoutPage;